/**
 * Projects settings page — wide sortable table view of every project in the
 * workspace. Card view stays untouched at /admin/projects; this is the admin
 * oversight table (locked 2026-07-21).
 *
 * Data source: existing GET /api/editor/projects — already returns every
 * field this page needs (name, coverImageUrl, inactiveAt, createdAt,
 * updatedAt, storageBytes, retentionDays). No new backend work.
 *
 * Actions per row use the existing ProjectCardMenu — same "Project Settings",
 * "Mark Inactive/Active", "Delete" surface as the card view. Row click
 * navigates to the project detail page.
 *
 * Sortable column headers deferred — default order is Last Updated DESC
 * (matches the card view's default) with inactive projects sunk to the
 * bottom. A search box + status filter cover the common admin needs.
 */

import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, FolderKanban, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { ProjectCardMenu } from "@/components/projects/project-card-menu";
import { SettingsPageHeader } from "@/pages/settings/settings-placeholder";
import { useProjects } from "@/hooks/use-projects";
import type { Project } from "@/lib/api-types";

type StatusFilter = "all" | "active" | "inactive";

function formatDate(value: string | undefined | null): string {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function formatBytes(n: number | undefined | null): string {
  if (typeof n !== "number" || !Number.isFinite(n) || n <= 0) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < units.length - 1) { v /= 1024; i++; }
  return `${v < 10 ? v.toFixed(2) : v < 100 ? v.toFixed(1) : Math.round(v)} ${units[i]}`;
}

function retentionLabel(days: Project["retentionDays"]): string {
  if (days === null || days === undefined) return "Off";
  return `${days} days`;
}

export function ProjectsSettingsPage() {
  const navigate = useNavigate();
  const projectsQuery = useProjects();
  const [q, setQ] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");

  const filtered = useMemo(() => {
    const rows = projectsQuery.data ?? [];
    const needle = q.trim().toLowerCase();

    const matched = rows.filter((p) => {
      if (status === "active" && p.inactiveAt) return false;
      if (status === "inactive" && !p.inactiveAt) return false;
      if (needle && !p.name.toLowerCase().includes(needle)) return false;
      return true;
    });

    // Default order: last updated DESC, inactive sunk to the bottom (matches
    // the card view rule locked in project_frontend_build_progress).
    const sorted = [...matched].sort((a, b) => {
      const aTime = new Date(a.updatedAt ?? a.createdAt ?? 0).getTime();
      const bTime = new Date(b.updatedAt ?? b.createdAt ?? 0).getTime();
      return bTime - aTime;
    });
    sorted.sort((a, b) => (a.inactiveAt ? 1 : 0) - (b.inactiveAt ? 1 : 0));
    return sorted;
  }, [projectsQuery.data, q, status]);

  return (
    <div>
      <SettingsPageHeader
        title="Projects"
        subtitle="All projects in this workspace."
      />

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search projects…"
            className="pl-9"
          />
        </div>
        <StatusFilterButton value={status} onChange={setStatus} />
      </div>

      <div className="overflow-x-auto rounded-2xl border border-border bg-background">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="border-b border-border bg-secondary/40 text-left text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-3 font-medium">Project</th>
              <th className="px-4 py-3 font-medium">Status</th>
              <th className="px-4 py-3 font-medium">Last updated</th>
              <th className="px-4 py-3 font-medium">Created</th>
              <th className="px-4 py-3 font-medium">Size</th>
              <th className="px-4 py-3 font-medium">Retention</th>
              <th className="w-10 px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {projectsQuery.isLoading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-muted-foreground">
                  Loading…
                </td>
              </tr>
            ) : projectsQuery.isError ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-destructive">
                  Couldn't load projects.
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-10 text-center text-sm text-muted-foreground">
                  {q || status !== "all" ? "No projects match those filters." : "No projects yet."}
                </td>
              </tr>
            ) : (
              filtered.map((project) => (
                <ProjectRow
                  key={project.id}
                  project={project}
                  onOpen={() => navigate(`/admin/projects/${project.id}`)}
                />
              ))
            )}
          </tbody>
        </table>
        <div className="border-t border-border px-4 py-2.5 text-xs text-muted-foreground">
          {filtered.length} {filtered.length === 1 ? "project" : "projects"}
          {filtered.length !== (projectsQuery.data?.length ?? 0) && (
            <> · filtered from {projectsQuery.data?.length ?? 0}</>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Row ──────────────────────────────────────────────────────────────────────

function ProjectRow({ project, onOpen }: { project: Project; onOpen: () => void }) {
  return (
    <tr
      onClick={onOpen}
      className={
        "cursor-pointer border-b border-border/60 transition-colors last:border-b-0 hover:bg-secondary/20 " +
        (project.inactiveAt ? "opacity-70" : "")
      }
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="grid h-9 w-12 shrink-0 place-items-center overflow-hidden rounded-md bg-secondary/60">
            {project.coverImageUrl ? (
              <img
                src={project.coverImageUrl}
                alt=""
                loading="lazy"
                className="h-full w-full object-cover"
              />
            ) : (
              <FolderKanban className="h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
            )}
          </div>
          <div className="min-w-0">
            <div className="truncate font-medium text-foreground">{project.name}</div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3">
        {project.inactiveAt ? (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-amber-400/40 bg-amber-400/10 px-2 py-0.5 text-xs font-medium text-amber-500">
            Inactive
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full border border-emerald-400/40 bg-emerald-400/10 px-2 py-0.5 text-xs font-medium text-emerald-500">
            Active
          </span>
        )}
      </td>
      <td className="px-4 py-3 text-muted-foreground">
        {formatDate(project.updatedAt ?? project.createdAt)}
      </td>
      <td className="px-4 py-3 text-muted-foreground">
        {formatDate(project.createdAt)}
      </td>
      <td className="px-4 py-3 text-muted-foreground tabular-nums">
        {formatBytes(project.storageBytes)}
      </td>
      <td className="px-4 py-3 text-muted-foreground">
        {retentionLabel(project.retentionDays)}
      </td>
      <td
        className="px-4 py-3 text-right"
        onClick={(e) => e.stopPropagation()}
      >
        <ProjectCardMenu project={project} />
      </td>
    </tr>
  );
}

// ── Status filter dropdown ───────────────────────────────────────────────────

function StatusFilterButton({
  value,
  onChange,
}: {
  value: StatusFilter;
  onChange: (v: StatusFilter) => void;
}) {
  const labels: Record<StatusFilter, string> = {
    all: "All statuses",
    active: "Active only",
    inactive: "Inactive only",
  };
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-9 shrink-0">
          {labels[value]}
          <ChevronDown className="ml-1.5 h-4 w-4" strokeWidth={2} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel>Filter by status</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {(["all", "active", "inactive"] as StatusFilter[]).map((v) => (
          <DropdownMenuCheckboxItem
            key={v}
            checked={value === v}
            onSelect={(e) => {
              e.preventDefault();
              onChange(v);
            }}
          >
            {labels[v]}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
